/**
 * TestingForm - Form component for internal translation testing
 * TRNS-143: Internal Testing UI
 */

import { useState, useEffect, useCallback } from 'react';
import {
  PipelineConfig,
  PromptWeighting,
  TestingContext,
} from '../../types';
import { saveFormState, loadFormState } from '../../services/testingService';
import './TestingForm.css';

// All 29 supported languages
const LANGUAGES = [
  { code: 'en', name: 'English' },
  { code: 'es', name: 'Spanish' },
  { code: 'fr', name: 'French' },
  { code: 'de', name: 'German' },
  { code: 'he', name: 'Hebrew' },
  { code: 'ar', name: 'Arabic' },
  { code: 'pt', name: 'Portuguese' },
  { code: 'it', name: 'Italian' },
  { code: 'ru', name: 'Russian' },
  { code: 'ja', name: 'Japanese' },
  { code: 'zh', name: 'Chinese' },
  { code: 'ko', name: 'Korean' },
  { code: 'nl', name: 'Dutch' },
  { code: 'pl', name: 'Polish' },
  { code: 'sv', name: 'Swedish' },
  { code: 'da', name: 'Danish' },
  { code: 'no', name: 'Norwegian' },
  { code: 'fi', name: 'Finnish' },
  { code: 'tr', name: 'Turkish' },
  { code: 'uk', name: 'Ukrainian' },
  { code: 'cs', name: 'Czech' },
  { code: 'ro', name: 'Romanian' },
  { code: 'hu', name: 'Hungarian' },
  { code: 'el', name: 'Greek' },
  { code: 'th', name: 'Thai' },
  { code: 'vi', name: 'Vietnamese' },
  { code: 'id', name: 'Indonesian' },
  { code: 'hi', name: 'Hindi' },
  { code: 'bn', name: 'Bengali' },
];

const MAX_CHARS = 10000;

interface TestingFormProps {
  onSubmit: (params: {
    text: string;
    sourceLang: string;
    targetLang: string;
    context: TestingContext;
    pipeline: PipelineConfig;
    weighting: PromptWeighting;
  }) => void;
  isLoading: boolean;
}

export function TestingForm({ onSubmit, isLoading }: TestingFormProps) {
  // Form state
  const [sourceText, setSourceText] = useState('');
  const [sourceLang, setSourceLang] = useState('en');
  const [targetLang, setTargetLang] = useState('es');
  const [langSearch, setLangSearch] = useState('');

  // Context
  const [gender, setGender] = useState<TestingContext['gender']>('neutral');
  const [register, setRegister] = useState<TestingContext['register']>('informal');
  const [tone, setTone] = useState<TestingContext['tone']>('casual');
  const [region, setRegion] = useState('');

  // Pipeline
  const [mode, setMode] = useState<'write' | 'understand' | 'be_understood'>('write');
  const [model, setModel] = useState('');
  const [temperature, setTemperature] = useState(0.7);
  const [topP, setTopP] = useState(0.9);
  const [maxTokens, setMaxTokens] = useState(2048);

  // Weighting
  const [accuracy, setAccuracy] = useState(50);

  // Load saved form state on mount
  useEffect(() => {
    const saved = loadFormState();
    if (saved) {
      if (saved.sourceText) setSourceText(saved.sourceText as string);
      if (saved.sourceLang) setSourceLang(saved.sourceLang as string);
      if (saved.targetLang) setTargetLang(saved.targetLang as string);
      if (saved.gender) setGender(saved.gender as TestingContext['gender']);
      if (saved.register) setRegister(saved.register as TestingContext['register']);
      if (saved.tone) setTone(saved.tone as TestingContext['tone']);
      if (saved.region) setRegion(saved.region as string);
      if (saved.mode) setMode(saved.mode as 'write' | 'understand' | 'be_understood');
      if (saved.model) setModel(saved.model as string);
      if (typeof saved.temperature === 'number') setTemperature(saved.temperature);
      if (typeof saved.topP === 'number') setTopP(saved.topP);
      if (typeof saved.maxTokens === 'number') setMaxTokens(saved.maxTokens);
      if (typeof saved.accuracy === 'number') setAccuracy(saved.accuracy);
    }
  }, []);

  // Persist form state on change
  const persistState = useCallback(() => {
    saveFormState({
      sourceText, sourceLang, targetLang,
      gender, register, tone, region,
      mode, model, temperature, topP, maxTokens,
      accuracy,
    });
  }, [sourceText, sourceLang, targetLang, gender, register, tone, region, mode, model, temperature, topP, maxTokens, accuracy]);

  useEffect(() => {
    persistState();
  }, [persistState]);

  const handleSwapLangs = () => {
    setSourceLang(targetLang);
    setTargetLang(sourceLang);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!sourceText.trim() || isLoading) return;

    onSubmit({
      text: sourceText,
      sourceLang,
      targetLang,
      context: { gender, register, tone, region: region || undefined },
      pipeline: {
        mode,
        model: model || undefined,
        temperature,
        topP,
        maxTokens,
      },
      weighting: { accuracy, naturalness: 100 - accuracy },
    });
  };

  // Apply a preset to the form
  const filteredLangs = langSearch
    ? LANGUAGES.filter((l) =>
        l.name.toLowerCase().includes(langSearch.toLowerCase()) ||
        l.code.toLowerCase().includes(langSearch.toLowerCase())
      )
    : LANGUAGES;

  return (
    <form className="testing-form" onSubmit={handleSubmit}>
      {/* Language Selection Card */}
      <div className="testing-card">
        <h3 className="card-title">Language Selection</h3>
        <div className="lang-search">
          <input
            type="text"
            placeholder="Filter languages..."
            value={langSearch}
            onChange={(e) => setLangSearch(e.target.value)}
            className="lang-search-input"
          />
        </div>
        <div className="lang-row">
          <div className="lang-select-group">
            <label>Source</label>
            <select
              value={sourceLang}
              onChange={(e) => setSourceLang(e.target.value)}
            >
              {filteredLangs.map((l) => (
                <option key={l.code} value={l.code}>
                  {l.name} ({l.code})
                </option>
              ))}
            </select>
          </div>
          <button
            type="button"
            className="swap-btn"
            onClick={handleSwapLangs}
            title="Swap languages"
          >
            &#8646;
          </button>
          <div className="lang-select-group">
            <label>Target</label>
            <select
              value={targetLang}
              onChange={(e) => setTargetLang(e.target.value)}
            >
              {filteredLangs.map((l) => (
                <option key={l.code} value={l.code}>
                  {l.name} ({l.code})
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Text Input Card */}
      <div className="testing-card">
        <h3 className="card-title">Source Text</h3>
        <textarea
          className="source-textarea"
          value={sourceText}
          onChange={(e) => {
            if (e.target.value.length <= MAX_CHARS) {
              setSourceText(e.target.value);
            }
          }}
          placeholder="Enter text to translate..."
          rows={6}
        />
        <div className="char-counter">
          {sourceText.length} / {MAX_CHARS}
        </div>
      </div>

      {/* Context Config Card */}
      <div className="testing-card">
        <h3 className="card-title">Context Configuration</h3>

        <div className="config-section">
          <label className="config-label">Gender</label>
          <div className="radio-group">
            {(['male', 'female', 'neutral', 'other'] as const).map((g) => (
              <label key={g} className="radio-item">
                <input
                  type="radio"
                  name="gender"
                  value={g}
                  checked={gender === g}
                  onChange={() => setGender(g)}
                />
                <span>{g}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="config-section">
          <label className="config-label">Register</label>
          <div className="radio-group">
            {(['formal', 'informal', 'academic', 'business'] as const).map((r) => (
              <label key={r} className="radio-item">
                <input
                  type="radio"
                  name="register"
                  value={r}
                  checked={register === r}
                  onChange={() => setRegister(r)}
                />
                <span>{r}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="config-section">
          <label className="config-label">Tone</label>
          <div className="radio-group">
            {(['professional', 'casual', 'friendly', 'authoritative'] as const).map((t) => (
              <label key={t} className="radio-item">
                <input
                  type="radio"
                  name="tone"
                  value={t}
                  checked={tone === t}
                  onChange={() => setTone(t)}
                />
                <span>{t}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="config-section">
          <label className="config-label">Region</label>
          <input
            type="text"
            value={region}
            onChange={(e) => setRegion(e.target.value)}
            placeholder="e.g. Latin America, Castilian, etc."
            className="region-input"
          />
        </div>
      </div>

      {/* Pipeline Config Card */}
      <div className="testing-card">
        <h3 className="card-title">Pipeline Configuration</h3>

        <div className="config-section">
          <label className="config-label">Mode</label>
          <div className="radio-group">
            <label className="radio-item">
              <input
                type="radio"
                name="mode"
                value="write"
                checked={mode === 'write'}
                onChange={() => setMode('write')}
              />
              <span>Write</span>
            </label>
            <label className="radio-item">
              <input
                type="radio"
                name="mode"
                value="understand"
                checked={mode === 'understand'}
                onChange={() => setMode('understand')}
              />
              <span>Understand</span>
            </label>
          </div>
        </div>

        <div className="config-section">
          <label className="config-label">Model (optional)</label>
          <input
            type="text"
            value={model}
            onChange={(e) => setModel(e.target.value)}
            placeholder="Leave blank for default pipeline"
            className="model-input"
          />
          <span className="config-hint">Pipeline config fields are sent but may not be parsed by backend yet</span>
        </div>

        <div className="config-section">
          <label className="config-label">Temperature: {temperature.toFixed(1)}</label>
          <input
            type="range"
            min="0"
            max="2"
            step="0.1"
            value={temperature}
            onChange={(e) => setTemperature(parseFloat(e.target.value))}
            className="slider"
          />
          <div className="slider-labels">
            <span>0</span>
            <span>2</span>
          </div>
        </div>

        <div className="config-section">
          <label className="config-label">Top-p: {topP.toFixed(2)}</label>
          <input
            type="range"
            min="0"
            max="1"
            step="0.05"
            value={topP}
            onChange={(e) => setTopP(parseFloat(e.target.value))}
            className="slider"
          />
          <div className="slider-labels">
            <span>0</span>
            <span>1</span>
          </div>
        </div>

        <div className="config-section">
          <label className="config-label">Max Tokens</label>
          <input
            type="number"
            value={maxTokens}
            onChange={(e) => setMaxTokens(parseInt(e.target.value, 10) || 2048)}
            min={1}
            max={8192}
            className="tokens-input"
          />
        </div>
      </div>

      {/* Prompt Weighting Card */}
      <div className="testing-card">
        <h3 className="card-title">Prompt Weighting</h3>
        <div className="config-section">
          <div className="weighting-labels">
            <span>Accuracy ({accuracy}%)</span>
            <span>Naturalness ({100 - accuracy}%)</span>
          </div>
          <input
            type="range"
            min="0"
            max="100"
            step="5"
            value={accuracy}
            onChange={(e) => setAccuracy(parseInt(e.target.value, 10))}
            className="slider"
          />
          <span className="config-hint">UI-only for now; backend integration planned</span>
        </div>
      </div>

      {/* Submit */}
      <button
        type="submit"
        className="submit-btn"
        disabled={isLoading || !sourceText.trim()}
      >
        {isLoading ? 'Running Translation...' : 'Run Translation'}
      </button>
    </form>
  );
}
